/**
 * Calendar Badges — monthly streaks + the yearly collection
 *
 * Unlike the static challenge badges (badges.ts), these are minted per calendar
 * period, so their ids carry the year and month:
 *   - `badge-month-YYYY-MM` — an active streak EVERY day of that calendar month
 *     (worth a common box).
 *   - `badge-year-YYYY` — collecting all twelve month badges of a calendar year
 *     (worth a legendary box + a bonus, i.e. "a lot").
 *
 * Earned ids live in StoreSettings.earnedBadgeIds alongside the challenge
 * badges; the grant logic is in ChallengeService. A "day" counts toward a month
 * streak when it met the daily goal OR was covered by a streak freeze — the same
 * notion of "covered" the streak UI uses (see globalStreakUtils).
 */

import type { Badge } from './badges';
import { getBadge } from './badges';
import type { ChallengeReward } from './challenges';

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
export const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
// One real glyph per month — seasonal-ish, and distinct from the challenge badges.
const MONTH_EMOJI = ['❄️', '💗', '🌱', '🌷', '🌸', '☀️', '🏖️', '🌻', '🍂', '🎃', '🍁', '🎄'];

/** Monthly streak → a common box. Yearly collection → "a lot". */
export const MONTHLY_REWARD: ChallengeReward = { box: 'common' };
export const YEARLY_REWARD: ChallengeReward = { box: 'legendary', points: 500 };

/** month1 is 1-based. "2026-06" → "badge-month-2026-06". */
export function monthlyBadgeId(year: number, month1: number): string {
  return `badge-month-${year}-${String(month1).padStart(2, '0')}`;
}
export function yearlyBadgeId(year: number): string {
  return `badge-year-${year}`;
}

const MONTHLY_RE = /^badge-month-(\d{4})-(\d{2})$/;
const YEARLY_RE = /^badge-year-(\d{4})$/;

export function parseMonthlyBadgeId(id: string): { year: number; month1: number } | null {
  const m = MONTHLY_RE.exec(id);
  return m ? { year: Number(m[1]), month1: Number(m[2]) } : null;
}
export function parseYearlyBadgeId(id: string): { year: number } | null {
  const m = YEARLY_RE.exec(id);
  return m ? { year: Number(m[1]) } : null;
}
export function isCalendarBadgeId(id: string): boolean {
  return MONTHLY_RE.test(id) || YEARLY_RE.test(id);
}

/** Resolve a dynamic calendar badge id to a Badge (parallels getBadge). */
export function getCalendarBadge(id: string): Badge | null {
  const mo = parseMonthlyBadgeId(id);
  if (mo) {
    const name = MONTH_NAMES[mo.month1 - 1];
    return {
      id,
      name: `${name} ${mo.year}`,
      emoji: MONTH_EMOJI[mo.month1 - 1],
      rarity: 'rare',
      blurb: `An active streak every day of ${name} ${mo.year}.`,
    };
  }
  const yr = parseYearlyBadgeId(id);
  if (yr) {
    return {
      id,
      name: `${yr.year} Perfect Year`,
      emoji: '🏆',
      rarity: 'legendary',
      blurb: `Every month of ${yr.year}, streak unbroken.`,
    };
  }
  return null;
}

/** Resolve any badge id — static challenge badges OR dynamic calendar badges. */
export function resolveBadge(id: string | undefined): Badge | null {
  if (!id) return null;
  return getBadge(id) ?? getCalendarBadge(id);
}

/** Days in a 1-based month (handles leap Februaries). */
export function daysInMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

export interface MonthCoverage {
  ym: string; // "YYYY-MM"
  year: number;
  month1: number;
  coveredDays: number;
  totalDays: number;
  ended: boolean; // the month's last day is <= today
  full: boolean; // ended AND every day covered → eligible for the month badge
}

/**
 * Per-month coverage for every month that has at least one covered day. A month
 * only becomes `full` once it has ended and every one of its days is covered.
 */
export function computeMonthCoverage(coveredDates: Set<string>, today: string): MonthCoverage[] {
  const daysByYm = new Map<string, Set<number>>();
  for (const d of coveredDates) {
    const ym = d.slice(0, 7);
    const day = Number(d.slice(8, 10));
    if (!daysByYm.has(ym)) daysByYm.set(ym, new Set());
    daysByYm.get(ym)!.add(day);
  }
  const out: MonthCoverage[] = [];
  for (const [ym, days] of daysByYm) {
    const year = Number(ym.slice(0, 4));
    const month1 = Number(ym.slice(5, 7));
    const totalDays = daysInMonth(year, month1);
    const lastDay = `${ym}-${String(totalDays).padStart(2, '0')}`;
    const ended = lastDay <= today;
    out.push({
      ym, year, month1,
      coveredDays: days.size,
      totalDays,
      ended,
      full: ended && days.size === totalDays,
    });
  }
  return out;
}

export interface CalendarMonthCell {
  ym: string;
  month1: number;
  coveredDays: number;
  totalDays: number;
  ended: boolean;
  earned: boolean;
}
export interface CalendarSummary {
  year: number;
  /** Always twelve cells, January → December. */
  months: CalendarMonthCell[];
  yearEarned: boolean;
}

/**
 * Build a full Jan→Dec grid for every relevant year (always the current year,
 * plus any year with covered days or an earned calendar badge), newest first —
 * for the dimmed "above each month" display.
 */
export function buildCalendarSummaries(
  coveredDates: Set<string>,
  earned: Set<string>,
  today: string,
): CalendarSummary[] {
  const years = new Set<number>([Number(today.slice(0, 4))]);
  for (const d of coveredDates) years.add(Number(d.slice(0, 4)));
  for (const id of earned) {
    const mo = parseMonthlyBadgeId(id);
    if (mo) { years.add(mo.year); continue; }
    const yr = parseYearlyBadgeId(id);
    if (yr) years.add(yr.year);
  }

  const countByYm = new Map<string, number>();
  for (const d of coveredDates) {
    const ym = d.slice(0, 7);
    countByYm.set(ym, (countByYm.get(ym) ?? 0) + 1);
  }

  return [...years].sort((a, b) => b - a).map(year => {
    const months: CalendarMonthCell[] = [];
    for (let m = 1; m <= 12; m++) {
      const ym = `${year}-${String(m).padStart(2, '0')}`;
      const totalDays = daysInMonth(year, m);
      const lastDay = `${ym}-${String(totalDays).padStart(2, '0')}`;
      months.push({
        ym,
        month1: m,
        coveredDays: countByYm.get(ym) ?? 0,
        totalDays,
        ended: lastDay <= today,
        earned: earned.has(monthlyBadgeId(year, m)),
      });
    }
    return { year, months, yearEarned: earned.has(yearlyBadgeId(year)) };
  });
}
